import React, { useState } from 'react'
import { Drawer, IconButton, Button, CircularProgress } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import UndoIcon from '@material-ui/icons/Undo'
import CheckIcon from '@material-ui/icons/Check'
import AuthService from 'tools/AuthService'
import strings from 'strings'
import { useTask } from './hooks/useTask'
import { formatExact, formatRelative, statusPillModifier } from './ctHelpers'
import RejectTaskModal from './modals/RejectTaskModal'
import ReassignTaskModal from './modals/ReassignTaskModal'
import CompleteTaskModal from './modals/CompleteTaskModal'

const TaskDetailDrawer = ({ open, onClose, taskId, applicationUuid, onAnyChange }) => {
    const t = strings.CT
    const { task, loading, refetch } = useTask(open ? taskId : null)
    const [rejectOpen, setRejectOpen] = useState(false)
    const [reassignOpen, setReassignOpen] = useState(false)
    const [completeOpen, setCompleteOpen] = useState(false)

    const currentUserEmail = (AuthService.loggedInUser?.email || '').toLowerCase()
    const isAssignee = task?.assignee?.email
        && task.assignee.email.toLowerCase() === currentUserEmail
    // Read-only users can view a task but never mutate it.
    const isReadOnly = AuthService.isReadOnlyUser && AuthService.isReadOnlyUser()
    const canAct = !isReadOnly

    const handleActionDone = () => {
        refetch()
        onAnyChange && onAnyChange()
    }

    return (
        <>
            <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: 'ct-drawer-paper' }}>
                {loading || !task ? (
                    <div className="ct-loading"><CircularProgress style={{ color: '#7B61FF' }} /></div>
                ) : (
                    <>
                        <div className="ct-drawer-status-row">
                            <span className={`ct-status-pill ct-status-pill--${statusPillModifier(task.status)}`}>
                                {t[`STATUS_${task.status}`] || task.status}
                            </span>
                            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                        </div>

                        <div className="ct-drawer-title">{task.title}</div>

                        <div className="ct-drawer-kv-grid">
                            <div>
                                <div className="ct-kv-label">{t.DRAWER_ASSIGNEE}</div>
                                {task.assignee ? (
                                    <div className="ct-kv-value">
                                        {task.assignee.name || task.assignee.email}
                                        {task.assignee.external && <span className="ct-ext-badge">{t.EXT_BADGE}</span>}
                                    </div>
                                ) : (
                                    <div className="ct-kv-value ct-kv-value--muted">{t.UNASSIGNED}</div>
                                )}
                            </div>
                            <div>
                                <div className="ct-kv-label">{t.DRAWER_ASSIGNER}</div>
                                <div className="ct-kv-value">
                                    {task.assigner?.name || task.assigner?.email || '—'}
                                </div>
                            </div>
                            <div>
                                <div className="ct-kv-label">{t.DRAWER_CREATED}</div>
                                <div className="ct-kv-value">{formatExact(task.createdAt)}</div>
                            </div>
                            <div>
                                <div className="ct-kv-label">{t.DRAWER_LAST_UPDATE}</div>
                                <div className="ct-kv-value">{formatRelative(task.updatedAt)}</div>
                            </div>
                        </div>

                        {task.description && (
                            <>
                                <div className="ct-drawer-section-label">{t.DRAWER_DESCRIPTION}</div>
                                <div style={{ fontSize: 13, color: 'var(--neutral-900)', lineHeight: 1.5 }}>{task.description}</div>
                            </>
                        )}

                        <div className="ct-drawer-section-label">{t.DRAWER_TIMELINE}</div>
                        <div className="ct-timeline">
                            {(task.timeline || []).map((ev, idx) => {
                                const isFirst = idx === 0
                                const dotKind = isFirst ? 'created' : ev.eventType.toLowerCase()
                                const icon = dotIcon(dotKind)
                                const label = isFirst ? t.EVENT_CREATED : (t[`EVENT_${ev.eventType}`] || ev.eventType)
                                return (
                                    <div key={ev.id} className="ct-event">
                                        <div className={`ct-event__dot ct-event__dot--${dotKind}`}>{icon}</div>
                                        <div className="ct-event__label">{label}</div>
                                        <div className="ct-event__actor">
                                            {ev.actor?.name || ev.actor?.email}
                                            {ev.actor?.external && <span className="ct-ext-badge">{t.EXT_BADGE}</span>}
                                        </div>
                                        <div className="ct-event__time">{formatExact(ev.occurredAt)} · {formatRelative(ev.occurredAt)}</div>
                                        {ev.reason && <div className="ct-event__reason">"{ev.reason}"</div>}
                                        {ev.notes && <div className="ct-event__reason" style={{ background: '#F0FDF4', borderLeftColor: 'var(--status-approved)', color: '#065F46', fontStyle: 'normal' }}>{ev.notes}</div>}
                                    </div>
                                )
                            })}
                        </div>

                        <div className="ct-drawer-actions">
                            {canAct && task.status === 'ASSIGNED' && isAssignee && (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<UndoIcon />}
                                        onClick={() => setRejectOpen(true)}
                                        style={{ textTransform: 'none', borderRadius: 10, borderColor: 'var(--status-declined)', color: 'var(--status-declined)' }}
                                    >
                                        {t.ACTION_REJECT}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<CheckIcon />}
                                        onClick={() => setCompleteOpen(true)}
                                        style={{ background: 'var(--status-approved)', color: '#fff', textTransform: 'none', borderRadius: 10, fontWeight: 600 }}
                                    >
                                        {t.ACTION_COMPLETE}
                                    </Button>
                                </>
                            )}
                            {canAct && task.status === 'REJECTED' && (
                                <Button
                                    variant="contained"
                                    className="ct-new-task-btn"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => setReassignOpen(true)}
                                >
                                    {t.ACTION_REASSIGN}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </Drawer>

            <RejectTaskModal
                open={rejectOpen}
                onClose={() => setRejectOpen(false)}
                task={task}
                onDone={handleActionDone}
            />
            <ReassignTaskModal
                open={reassignOpen}
                onClose={() => setReassignOpen(false)}
                task={task}
                applicationUuid={applicationUuid}
                onDone={handleActionDone}
            />
            <CompleteTaskModal
                open={completeOpen}
                onClose={() => setCompleteOpen(false)}
                task={task}
                onDone={handleActionDone}
            />
        </>
    )
}

const dotIcon = (kind) => {
    switch (kind) {
        case 'created':   return <AddIcon style={{ fontSize: 12 }} />
        case 'assigned':  return <ArrowForwardIcon style={{ fontSize: 12 }} />
        case 'rejected':  return <UndoIcon style={{ fontSize: 12 }} />
        case 'completed': return <CheckIcon style={{ fontSize: 12 }} />
        default:          return null
    }
}

export default TaskDetailDrawer
